import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  alpha,
  Avatar,
  Rating,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Store as StoreIcon,
  Star as StarIcon,
} from '@mui/icons-material';

const Proveedores = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const columns = [
    { field: 'nombre', headerName: 'Proveedor', width: 250, renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
          <StoreIcon />
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
          <Typography variant="caption" color="text.secondary">{params.row.cuit}</Typography>
        </Box>
      </Box>
    )},
    { field: 'rubro', headerName: 'Rubro', width: 150 },
    { field: 'localidad', headerName: 'Localidad', width: 150 },
    { field: 'calificacion', headerName: 'Calificación', width: 140, renderCell: (params) => (
      <Rating value={params.value} readOnly size="small" />
    )},
    { field: 'estado', headerName: 'Estado', width: 120, renderCell: (params) => (
      <Chip label={params.value} size="small" color={params.value === 'Activo' ? 'success' : params.value === 'Suspendido' ? 'error' : 'warning'} />
    )},
    { field: 'volumen', headerName: 'Volumen 2025', width: 130, align: 'right', headerAlign: 'right', renderCell: (params) => (
      <Typography variant="body2" fontWeight={600}>${params.value}</Typography>
    )},
    { field: 'acciones', headerName: 'Acciones', width: 120, sortable: false, renderCell: () => (
      <Box>
        <IconButton size="small"><ViewIcon fontSize="small" /></IconButton>
        <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
      </Box>
    )},
  ];

  const rows = [
    { id: 1, nombre: 'Servicios HSE SRL', cuit: '30-71234567-8', rubro: 'Seguridad', localidad: 'Neuquén', calificacion: 5, estado: 'Activo', volumen: '2.5M' },
    { id: 2, nombre: 'Transporte Petrolero SA', cuit: '30-70987654-2', rubro: 'Logística', localidad: 'Añelo', calificacion: 4, estado: 'Activo', volumen: '4.8M' },
    { id: 3, nombre: 'Suministros Oil', cuit: '20-25678901-4', rubro: 'Materiales', localidad: 'Neuquén', calificacion: 4, estado: 'Activo', volumen: '3.2M' },
    { id: 4, nombre: 'Soldaduras Vaca Muerta', cuit: '30-71567890-1', rubro: 'Servicios', localidad: 'Rincón', calificacion: 3, estado: 'Evaluación', volumen: '850K' },
    { id: 5, nombre: 'Equipos y Herramientas', cuit: '30-70123456-9', rubro: 'Equipamiento', localidad: 'Neuquén', calificacion: 5, estado: 'Activo', volumen: '1.8M' },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Proveedores
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de proveedores y evaluaciones
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nuevo Proveedor
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary">85</Typography>
              <Typography variant="body2" color="text.secondary">Total Proveedores</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="success.main">72</Typography>
              <Typography variant="body2" color="text.secondary">Activos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">8</Typography>
              <Typography variant="body2" color="text.secondary">En Evaluación</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="h4" fontWeight={700} color="info.main">4.2</Typography>
                <StarIcon color="warning" />
              </Box>
              <Typography variant="body2" color="text.secondary">Calificación Promedio</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla */}
      <Card>
        <CardContent>
          <Box sx={{ height: 500 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              disableSelectionOnClick
              slots={{ toolbar: GridToolbar }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nuevo Proveedor</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Razón Social" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="CUIT" placeholder="30-12345678-9" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Rubro</InputLabel>
                <Select label="Rubro">
                  <MenuItem value="seguridad">Seguridad/HSE</MenuItem>
                  <MenuItem value="logistica">Logística</MenuItem>
                  <MenuItem value="materiales">Materiales</MenuItem>
                  <MenuItem value="servicios">Servicios</MenuItem>
                  <MenuItem value="equipamiento">Equipamiento</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Dirección" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Localidad" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Teléfono" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Email" type="email" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Contacto Principal" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observaciones" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Proveedores;
